import React, { useCallback } from 'react';
import type { CrudEditModalProps, CrudTableColumn } from '../crud/CrudTable';
import CrudTable, { useCrudState, useStandardCrudHandlers } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import type { AbortableRequest } from '@dr-pam/common-components/Utils/FetchUtils';
import {
	ActionIcon,
	Button,
	Container,
	Divider,
	Flex,
	Group,
	Loader,
	Menu,
	NumberInput,
	Select,
	Stack,
	Text,
	TextInput,
} from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEventService } from '../../services/EventService';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { EventOccurrenceSingleFragment, EventOccurrenceContentFragmentFragment } from '../../graphql/graphql';
import { DateTimePicker } from '@mantine/dates';
import DateTimeUtils, { DATE_TIME_FORMAT } from '@dr-pam/common-components/Utils/DateTimeUtils';
import type { AddEventOccurrenceForm } from './AddEventOccurrenceModal';
import { validationSchema } from './AddEventOccurrenceModal';
import dayjs from 'dayjs';
import AddEventOccurrenceContentModal from './AddEventOccurrenceContentModal';
import EditEventOccurrenceContentModal from './EditEventOccurrenceContentModal';
import { IconDotsVertical, IconPencil, IconTrash } from '@tabler/icons-react';

export type EditEventOccurrenceModalProps = CrudEditModalProps<
	EventOccurrenceSingleFragment,
	EventOccurrenceSingleFragment
>;

export default function EditEventOccurrenceModal(props: EditEventOccurrenceModalProps) {
	const { modalId, onCancel, onEdited, current } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();
	const eventService = useEventService();

	// Only the top modal is mounted, so this one is remounted after each content block modal closes.
	// Fetching the blocks here, rather than copying them from `current`, is what makes a new block appear.
	const fetchContents = useCallback((): AbortableRequest<EventOccurrenceContentFragmentFragment[]> | null => {
		return eventService.getOccurrenceContents(current.id);
	}, [eventService, current.id]);

	const contentCrudState = useCrudState<
		EventOccurrenceContentFragmentFragment,
		EventOccurrenceContentFragmentFragment
	>(fetchContents);

	const contentCrudHandlers = useStandardCrudHandlers({
		crudState: contentCrudState,
		entityName: 'content block',
		nameAccessor: (c) => c.subtitle ?? 'content block',
		addModalFactory: (p) => (
			<AddEventOccurrenceContentModal
				{...p}
				eventOccurrenceId={current.id}
				sortOrder={contentCrudState.items?.length ?? 0}
			/>
		),
		editModalFactory: (p) => <EditEventOccurrenceContentModal {...p} />,
		delete: (c) => eventService.deleteOccurrenceContent(c.id),
	});

	const contentHeaders: CrudTableColumn[] = [{ children: 'Subtitle' }, { children: 'Image' }, { children: '' }];

	const contentColumns = (c: EventOccurrenceContentFragmentFragment): CrudTableColumn[] => [
		{ children: <Text size="sm">{c.subtitle ?? '—'}</Text> },
		{ children: <Text size="sm">{c.imageUrl ? '✓' : '—'}</Text> },
		{
			children: (
				<Flex justify="flex-end">
					<Menu shadow="md" width={160}>
						<Menu.Target>
							<ActionIcon color="dark" variant="subtle">
								<IconDotsVertical />
							</ActionIcon>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item leftSection={<IconPencil />} onClick={() => contentCrudHandlers.editHandler(c)}>
								Edit
							</Menu.Item>
							<Menu.Item
								leftSection={<IconTrash />}
								onClick={() => contentCrudHandlers.deleteHandler(c)}
								color="red"
							>
								Delete
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</Flex>
			),
		},
	];

	const form = useForm<EditEventOccurrenceForm>({
		initialValues: {
			start: typeof current.start === 'string' ? dayjs(current.start).toDate() : current.start,
			end: typeof current.end === 'string' ? dayjs(current.end).toDate() : (current.end ?? null),
			maxRegistrations: current.maxRegistrations ?? null,
			title: current.title ?? null,
			timezone: DateTimeUtils.getTimezoneInfo().name,
		},
		validate: schemaResolver(validationSchema),
		transformValues(values) {
			return {
				...values,
				maxRegistrations: values.maxRegistrations ? parseInt(`${values.maxRegistrations}`, 10) : null,
			};
		},
	});

	const handleSubmit = async (values: EditEventOccurrenceForm) => {
		const loader = addLoader();

		try {
			const start = DateTimeUtils.parseTz(
				DateTimeUtils.format(values.start, DATE_TIME_FORMAT),
				values.timezone,
				DATE_TIME_FORMAT,
			);
			const end = values.end
				? DateTimeUtils.parseTz(
						DateTimeUtils.format(values.end, DATE_TIME_FORMAT),
						values.timezone,
						DATE_TIME_FORMAT,
					)
				: null;

			const updatedEventOccurrence = await eventService.updateOccurrence(current.id, {
				start: { set: start },
				end: { set: end },
				maxRegistrations: { set: values.maxRegistrations },
				title: { set: values.title },
			});
			onEdited(updatedEventOccurrence);
			NotificationUtils.showSuccess('Successfully updated event occurrence', 'Event Occurrence');
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to update event occurrence');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	const allTimezones = DateTimeUtils.getTimezones().map((tz) => ({ value: tz, label: tz }));

	return (
		<Container size="md">
			<Stack>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<DateTimePicker
							{...form.getInputProps('start')}
							label="Start Date/Time"
							description="(DD-MM-YYYY hh:mm AM/PM)"
							valueFormat={DATE_TIME_FORMAT}
							timePickerProps={{ format: '12h' }}
							popoverProps={{ withinPortal: true }}
							disabled={isLoading}
							withAsterisk
							clearable
						/>
						<DateTimePicker
							{...form.getInputProps('end')}
							label="End Date/Time"
							description="(DD-MM-YYYY hh:mm AM/PM). Optional"
							valueFormat={DATE_TIME_FORMAT}
							timePickerProps={{ format: '12h' }}
							popoverProps={{ withinPortal: true }}
							disabled={isLoading}
							clearable
						/>
						<Select
							{...form.getInputProps('timezone')}
							data={allTimezones}
							label="Timezone"
							placeholder={'--- Please select ---'}
							nothingFoundMessage="No search results"
							disabled={isLoading}
							searchable
							clearable
						/>
						<TextInput
							{...form.getInputProps('title')}
							label="Title"
							description="e.g. talk topic. Optional"
							disabled={isLoading}
						/>
						<NumberInput
							{...form.getInputProps('maxRegistrations')}
							label="Maximum registrations"
							description="The maximum number of people that may register for this event occurrence"
							decimalScale={0}
							thousandSeparator=","
							disabled={isLoading}
						/>
						<Group justify="flex-end">
							<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading}>
								Save event {isLoading && <Loader size="xs" ml="xs" />}
							</Button>
						</Group>
					</Stack>
				</form>

				<Divider />

				<CrudTable
					items={contentCrudState.items ?? []}
					isLoading={contentCrudState.isFetching}
					title={{ order: 5, children: 'Content blocks' }}
					headers={contentHeaders}
					columns={contentColumns}
					entityName="content block"
					onAddClicked={contentCrudHandlers.addHandler}
					addButtonText="Add content block"
					padding={0}
				/>
			</Stack>
		</Container>
	);
}

export type EditEventOccurrenceForm = AddEventOccurrenceForm;
